const routes = {
  home: {
    style: 'home',
    paths: ['/'],
  },
  'meet-the-team': {
    style: 'about',
    paths: ['/meet-the-team/'],
  },
  'our-story': {
    style: 'about',
    paths: ['/our-story/'],
  },
  'our-process': {
    style: 'process',
    paths: ['/our-process/'],
  },
  blog: {
    style: 'blog',
    paths: ['/blog/'],
  },
  'blog-post': {
    style: 'blog',
  },
  faq: {
    style: 'faq',
    paths: ['/faq/'],
  },
  products: {
    style: 'products',
    paths: ['/products/'],
  },
  product_roomvo: {
    style: 'products',
  },
  product_custom: {
    style: 'products',
  },
  division: {
    style: 'divisions',
  },
  projects: {
    style: 'projects',
    paths: ['/projects/'],
  },
  gallery: {
    style: 'gallery',
  },
  testimonials: {
    style: 'testimonials',
    paths: ['/testimonials/'],
  },
  reviews: {
    style: 'reviews',
    paths: ['/reviews/'],
  },
  inventory: {
    style: 'inventory',
    paths: ['/inventory/'],
  },
  'request-an-estimate': {
    style: 'contact',
    paths: ['/request-an-estimate/'],
  },
  quiz: {
    style: 'quiz',
    paths: ['/quiz/'],
  },
}

const pathToNamespace = new Map()

for (const [namespace, route] of Object.entries(routes)) {
  for (const routePath of route.paths ?? []) {
    pathToNamespace.set(routePath, namespace)
  }
}

const pendingPageStyles = new Map()

const getPageStyleHref = styleName => `/assets/css/pages/${styleName}.bundle.css`

const findPageStyleLink = (styleName, attribute = 'data-page-style') =>
  document.querySelector(`link[${attribute}="${styleName}"]`)

const isStyleReady = styleName => {
  const link = findPageStyleLink(styleName)
  return Boolean(link?.sheet)
}

const loadedPageStyles = new Set(
  [...document.querySelectorAll('link[data-page-style]')]
    .filter(link => link.sheet)
    .map(link => link.dataset.pageStyle)
)

const resolveNamespace = pathname => {
  const path = pathname === '/' ? '/' : pathname.endsWith('/') ? pathname : `${pathname}/`
  if (pathToNamespace.has(path)) return pathToNamespace.get(path)
  if (path.startsWith('/blog/') && path !== '/blog/') return 'blog-post'
  if (path.startsWith('/projects/') && path !== '/projects/') return 'gallery'
  if (path.startsWith('/divisions/') && path !== '/divisions/') return 'division'
  if (path.startsWith('/products/') && path !== '/products/') return 'products'
  if (path.startsWith('/quiz/')) return 'quiz'
  return null
}

const styleForAnchor = anchor => {
  if (!anchor || anchor.target === '_blank') return null

  const url = new URL(anchor.href, location.origin)
  if (url.origin !== location.origin) return null

  const namespace = resolveNamespace(url.pathname)
  if (!namespace) return null

  return routes[namespace]?.style ?? null
}

export const loadPageStylesheet = styleName => {
  if (!styleName) return Promise.resolve()

  if (loadedPageStyles.has(styleName) && isStyleReady(styleName)) {
    return Promise.resolve()
  }

  if (pendingPageStyles.has(styleName)) {
    return pendingPageStyles.get(styleName)
  }

  const promise = new Promise(resolve => {
    const finish = () => {
      loadedPageStyles.add(styleName)
      pendingPageStyles.delete(styleName)
      resolve()
    }

    const waitForLink = link => {
      if (link.sheet) {
        finish()
        return
      }

      link.addEventListener('load', finish, { once: true })
      link.addEventListener('error', finish, { once: true })
    }

    const existing = findPageStyleLink(styleName)

    if (existing) {
      waitForLink(existing)
      return
    }

    const preload = findPageStyleLink(styleName, 'data-page-style-preload')
    const link = preload ?? document.createElement('link')

    link.rel = 'stylesheet'
    link.href = getPageStyleHref(styleName)
    link.removeAttribute('as')
    link.removeAttribute('data-page-style-preload')
    link.setAttribute('data-page-style', styleName)

    if (!preload) document.head.appendChild(link)

    waitForLink(link)
  })

  pendingPageStyles.set(styleName, promise)
  return promise
}

export const loadPageStyle = namespace =>
  loadPageStylesheet(routes[namespace]?.style)

const prefetchPageStyle = event => {
  if (!(event.target instanceof Element)) return

  const style = styleForAnchor(event.target.closest('a[href]'))
  if (!style) return

  void loadPageStylesheet(style)
}

export const pageAssetPrefetch = () => ({
  name: 'pageAssetPrefetch',
  init() {
    document.addEventListener('pointerenter', prefetchPageStyle, true)
    document.addEventListener('touchstart', prefetchPageStyle, { capture: true, passive: true })
  },
})

export const pageCssForPath = pathname => {
  const namespace = resolveNamespace(pathname)
  return namespace ? routes[namespace]?.style : null
}
